/**
 * 系统健康透视 — `/system/health-perspective`.
 *
 * One page answering, for one window: which downstream SKUs are served, who
 * is carrying each of them, whether the fallback ladder fired, and what the
 * blended margin is. The model those questions are answered from is
 * `types.ts`; this file is the shell — window selection, refresh, layout,
 * and the two degradation surfaces.
 *
 * ── Why there is no map ──
 *
 * This page rendered a pan/zoom SVG graph until it met production data: fan-
 * out 1–4, exactly one of 73 upstream models shared by more than one SKU —
 * 49 disjoint little trees — and at ~50 SKUs the upstream column did not fit
 * on screen at the default zoom. The canvas is gone; `ChainTable` renders one
 * row per SKU with its retry ladder inline. Nothing here needs geometry.
 *
 * ── Why the degradation banner is not optional chrome ──
 *
 * The page composes four independent reads. Losing one must not blank the
 * other three, and it must not look like health either: a missing money
 * layer that quietly renders as a healthy table is precisely the failure this
 * page exists to prevent. So `model.failures` is named, per slice, above the
 * fold, and every consumer of a degraded field renders unknown rather than a
 * substituted zero.
 *
 * ── URL state ──
 *
 * `?window=`, `?scope=` and `?node=` so a finding is a link someone else can
 * open. All three parse by narrowing (unknown → default / no selection) and
 * never throw, following `pages/metrics/hubState.ts`.
 */

import { Calendar, CalendarRange, Clock, RefreshCw, SearchX, Timer, TriangleAlert } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { formatLocalTimestamp } from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import {
  Button,
  Chip,
  EmptyState,
  InfoHint,
  PageHeader,
  Panel,
  Skeleton,
  Switch,
} from '../../ui/index.ts';
import { ChainTable } from './ChainTable.tsx';
import { OutcomeLegend } from './OutcomeBar.tsx';
import { NodeInspector } from './NodeInspector.tsx';
import { SummaryStrip } from './SummaryStrip.tsx';
import { UnattributedPanel } from './UnattributedPanel.tsx';
import { formatInteger, formatRate } from './display.ts';
import { PERSPECTIVE_SCOPES, parseScope, partitionUnattributed, scopeModel } from './scope.ts';
import type { PerspectiveScope } from './scope.ts';
import { useHealthPerspective } from './useHealthPerspective.ts';
import { PERSPECTIVE_WINDOWS } from './types.ts';
import type { PerspectiveModel, PerspectiveWindow, SliceFailure, TriagedRisk } from './types.ts';
import { useState } from 'react';

/**
 * The window a bare `/system/health-perspective` opens on.
 *
 * One hour, not a day. This page is read to answer "is it healthy right now",
 * and a 24h window answers a different question: it averages an incident that
 * started ten minutes ago into twenty-three hours of health, so the ladder and
 * the error rates both read normal while something is actively broken. The
 * longer windows stay one click away for trend work.
 */
const DEFAULT_WINDOW: PerspectiveWindow = '1h';

const SCOPE_LABEL_KEY: Readonly<Record<PerspectiveScope, I18nKey>> = {
  traffic: 'health_perspective.scope.traffic',
  problems: 'health_perspective.scope.problems',
  all: 'health_perspective.scope.all',
};

const WINDOW_LABEL_KEY: Readonly<Record<PerspectiveWindow, I18nKey>> = {
  '5m': 'health_perspective.window.5m',
  '1h': 'health_perspective.window.1h',
  '24h': 'health_perspective.window.24h',
  '7d': 'health_perspective.window.7d',
};

const WINDOW_ICON: Readonly<Record<PerspectiveWindow, LucideIcon>> = {
  '5m': Timer,
  '1h': Clock,
  '24h': Calendar,
  '7d': CalendarRange,
};

const FAILURE_KEY: Readonly<Record<SliceFailure['slice'], I18nKey>> = {
  traffic: 'health_perspective.degraded.traffic',
  'money-sku': 'health_perspective.degraded.money_sku',
  'money-upstream': 'health_perspective.degraded.money_upstream',
  routing: 'health_perspective.degraded.routing',
  upstreams: 'health_perspective.degraded.upstreams',
};

const RISK_KIND_KEY: Readonly<Record<TriagedRisk['kind'], I18nKey>> = {
  error_rate: 'health_perspective.risks.kind.error_rate',
  fallback_rate: 'health_perspective.risks.kind.fallback_rate',
  slow_first_byte_rate: 'health_perspective.risks.kind.slow_first_byte_rate',
};

export function HealthPerspectivePage(): JSX.Element {
  const t = useT();
  const [params, setParams] = useSearchParams();
  // Off by default (#2278): a poll re-runs the traffic overview AND re-keys
  // the two money slices; a page left open cost the shared RDS ~8% CPU.
  const [autoRefresh, setAutoRefresh] = useState(false);
  const window = parseWindow(params.get('window'));
  const scope = parseScope(params.get('scope'));
  const selectedNodeId = parseNode(params.get('node'));
  const state = useHealthPerspective(window, { autoRefresh });

  function setWindow(next: PerspectiveWindow): void {
    const clone = new URLSearchParams(params);
    clone.set('window', next);
    setParams(clone, { replace: true });
  }

  function setScope(next: PerspectiveScope): void {
    const clone = new URLSearchParams(params);
    clone.set('scope', next);
    setParams(clone, { replace: true });
  }

  function selectNode(next: string | null): void {
    const clone = new URLSearchParams(params);
    if (next === null) clone.delete('node');
    else clone.set('node', next);
    setParams(clone, { replace: true });
  }

  return (
    <section className="flex min-w-0 flex-col gap-6" data-testid="system-health-perspective-page">
      <PageHeader
        title={t('health_perspective.title')}
        sub={t('health_perspective.sub')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1" role="tablist" aria-label={t('health_perspective.window.aria')}>
              {PERSPECTIVE_WINDOWS.map((preset) => {
                const active = preset === window;
                const Glyph = WINDOW_ICON[preset];
                return (
                  <Button
                    key={preset}
                    aria-pressed={active}
                    variant={active ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => setWindow(preset)}
                    data-testid={`health-perspective-window-${preset}`}
                  >
                    <Glyph aria-hidden="true" className="h-3.5 w-3.5" />
                    {t(WINDOW_LABEL_KEY[preset])}
                  </Button>
                );
              })}
            </div>
            {state.model !== null ? (
              // With auto refresh off by default (#2278) the map can be old;
              // say when it was generated right next to the controls that
              // would refresh it, not only in the ladder footer.
              <span
                className="font-mono text-label text-paper-low"
                data-testid="health-perspective-generated-at"
              >
                {formatLocalTimestamp(state.model.generatedAt, { seconds: true })}
              </span>
            ) : null}
            <Switch
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
              label={t('health_perspective.auto_refresh')}
              testId="health-perspective-auto-refresh"
            />
            <Button
              variant="secondary"
              loading={state.isFetching}
              onClick={state.refetch}
              data-testid="health-perspective-refresh"
            >
              <RefreshCw aria-hidden="true" className="h-3.5 w-3.5" />
              {state.isFetching ? t('health_perspective.refreshing') : t('health_perspective.refresh')}
            </Button>
          </div>
        }
      />

      {state.isLoading ? (
        <LoadingView label={t('health_perspective.loading')} />
      ) : state.error !== null || state.model === null ? (
        <EmptyState
          testId="health-perspective-error"
          icon={<TriangleAlert aria-hidden="true" className="h-5 w-5 text-bad" />}
          title={t('health_perspective.error.title')}
          hint={t('health_perspective.error.hint')}
          action={
            <Button variant="primary" onClick={state.refetch}>
              {t('health_perspective.refresh')}
            </Button>
          }
        />
      ) : (
        <ReadyView
          model={state.model}
          scope={scope}
          onScope={setScope}
          selectedNodeId={selectedNodeId}
          onSelectNode={selectNode}
        />
      )}
    </section>
  );
}

function ReadyView({
  model,
  scope,
  onScope,
  selectedNodeId,
  onSelectNode,
}: {
  readonly model: PerspectiveModel;
  readonly scope: PerspectiveScope;
  readonly onScope: (scope: PerspectiveScope) => void;
  readonly selectedNodeId: string | null;
  readonly onSelectNode: (nodeId: string | null) => void;
}): JSX.Element {
  const t = useT();
  // The `skuId === null` bucket leaves the table before scoping: it is not a
  // SKU, it has no ladder, and its free `count_tokens` volume would dilute
  // every rate the table is about. It gets its own panel — see `scope.ts`.
  const { model: skuModel, unattributed } = partitionUnattributed(model);
  const scoped = scopeModel(skuModel, scope);
  // Nothing anywhere vs nothing in THIS scope are different statements and
  // get different empty states — telling an operator "no traffic and no
  // routing targets" when they have merely filtered the catalog out would
  // be a false report about the fleet.
  const fleetEmpty = skuModel.skus.length === 0 && skuModel.channels.length === 0;
  const scopeEmpty = !fleetEmpty && scoped.model.skus.length === 0;
  return (
    <>
      <ProvenanceLine model={model} />
      <DegradationBanner failures={model.failures} />
      {/* Unscoped and UNPARTITIONED, always: every headline figure still
          covers the whole fleet, unattributed requests included. See the
          invariant in `scope.ts`. */}
      <SummaryStrip summary={model.summary} />
      {unattributed === null || unattributed.traffic.requests === 0 ? null : (
        <UnattributedPanel node={unattributed} />
      )}
      {/* The table takes the full width — it is the widest thing on the page
          and the reason the page exists. The inspector sits BELOW it rather
          than in a side column, where it was squeezing the table into two
          thirds of the viewport to hold four sections in a narrow strip. */}
      <div className="flex min-w-0 flex-col gap-6">
        <Panel
          caption={t('health_perspective.table.title')}
          testId="health-perspective-table-panel"
          className="min-w-0"
          actions={
            <ScopeControl
              scope={scope}
              onScope={onScope}
              shown={scoped.shownSkuCount}
              total={scoped.totalSkuCount}
            />
          }
        >
          {fleetEmpty ? (
            <EmptyState
              testId="health-perspective-fleet-empty"
              icon={<SearchX aria-hidden="true" className="h-5 w-5" />}
              title={t('health_perspective.table.empty.title')}
              hint={t('health_perspective.table.empty.hint')}
            />
          ) : scopeEmpty ? (
            <EmptyState
              testId="health-perspective-scope-empty"
              icon={<SearchX aria-hidden="true" className="h-5 w-5" />}
              title={t('health_perspective.scope.empty.title')}
              hint={t('health_perspective.scope.empty.hint')}
              action={
                <Button variant="primary" onClick={() => onScope('all')}>
                  {t('health_perspective.scope.all')}
                </Button>
              }
            />
          ) : (
            <>
              {/* What the shapes and the two verdicts mean, in words, above
                  the table. Not optional chrome: the bar encodes magnitude on
                  a sqrt scale, and a non-linear scale nobody disclosed is a
                  lie about magnitude. The subject note is here for the same
                  reason — reading 客户侧失败 as a supply verdict is the exact
                  mistake this layout was rebuilt to prevent. */}
              <div className="flex flex-col gap-1.5 border-b border-line-soft px-4 py-2">
                <OutcomeLegend />
                <p
                  className="m-0 text-micro text-paper-low"
                  data-testid="health-perspective-subject-note"
                >
                  {t('health_perspective.table.subject_note')}
                </p>
                <p className="m-0 text-micro text-paper-low" data-testid="health-perspective-axis-note">
                  {t('health_perspective.table.ladder_axis_note')}
                </p>
                <p className="m-0 text-micro text-paper-low">
                  {t('health_perspective.table.default_order')}
                </p>
              </div>
              <ChainTable
                skus={scoped.model.skus}
                links={scoped.model.links}
                upstreamModels={scoped.model.upstreamModels}
                legsReported={model.skuChannelsReported}
                selectedNodeId={selectedNodeId}
                onSelectNode={(nodeId) => onSelectNode(nodeId === selectedNodeId ? null : nodeId)}
              />
            </>
          )}
        </Panel>
        {/* The inspector gets the UNPARTITIONED, UNSCOPED model so a `?node=`
            deep link still resolves when that node sits outside the current
            scope — including a link written against the unattributed bucket,
            which no longer has a row to be selected from. */}
        <NodeInspector model={model} selectedNodeId={selectedNodeId} />
      </div>
      <RisksPanel risks={model.risks} />
    </>
  );
}

/**
 * Scope pills, in the window selector's idiom, living in the TABLE panel's
 * caption bar — placement is half the message. The other half is the count
 * label, which names 表格 / "Table" in the visible text rather than hiding
 * that scope-is-table-only in a tooltip; the hint carries the long form.
 */
function ScopeControl({
  scope,
  onScope,
  shown,
  total,
}: {
  readonly scope: PerspectiveScope;
  readonly onScope: (scope: PerspectiveScope) => void;
  readonly shown: number;
  readonly total: number;
}): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex flex-wrap gap-1" role="tablist" aria-label={t('health_perspective.scope.aria')}>
        {PERSPECTIVE_SCOPES.map((option) => (
          <Button
            key={option}
            aria-pressed={option === scope}
            variant={option === scope ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => onScope(option)}
            data-testid={`health-perspective-scope-${option}`}
          >
            {t(SCOPE_LABEL_KEY[option])}
          </Button>
        ))}
      </div>
      <span className="font-mono text-micro text-paper-low" data-testid="health-perspective-scope-count">
        {t('health_perspective.scope.count', {
          shown: formatInteger(shown),
          total: formatInteger(total),
        })}
      </span>
      <InfoHint
        label={t('health_perspective.scope.aria')}
        content={t('health_perspective.scope.note')}
        testId="health-perspective-scope-note"
      />
    </div>
  );
}

/**
 * Where the numbers came from: the resolved interval both data layers were
 * asked for, when the snapshot was taken, and which routing config supplied
 * the ladders. An unattributed table is a table an operator cannot check.
 */
function ProvenanceLine({ model }: { readonly model: PerspectiveModel }): JSX.Element {
  const t = useT();
  return (
    <p className="m-0 flex flex-wrap gap-x-4 gap-y-1 text-dense text-paper-low">
      <span className="font-mono">
        {t('health_perspective.window.bounds', {
          from: formatLocalTimestamp(model.window.from, { seconds: true }),
          to: formatLocalTimestamp(model.window.to, { seconds: true }),
        })}
      </span>
      <span className="font-mono">
        {t('health_perspective.generated_at', {
          time: formatLocalTimestamp(model.generatedAt, { seconds: true }),
        })}
      </span>
      <span>
        {model.routingConfigName === null
          ? t('health_perspective.routing_source_unknown')
          : t('health_perspective.routing_source', { name: model.routingConfigName })}
      </span>
    </p>
  );
}

function DegradationBanner({ failures }: { readonly failures: readonly SliceFailure[] }): JSX.Element | null {
  const t = useT();
  if (failures.length === 0) return null;
  return (
    <div
      role="status"
      className="flex gap-3 rounded-lg border border-warn-line bg-warn-dim/40 p-4"
      data-testid="health-perspective-degraded"
    >
      <TriangleAlert aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-warn" />
      <div className="min-w-0">
        <p className="m-0 text-body font-medium text-warn">{t('health_perspective.degraded.title')}</p>
        <ul className="m-0 mt-1.5 flex list-none flex-col gap-1 p-0">
          {failures.map((failure) => (
            <li
              key={failure.slice}
              className="text-dense text-paper-mid"
              data-testid={`health-perspective-degraded-${failure.slice}`}
            >
              {t(FAILURE_KEY[failure.slice])}
              {failure.message.length === 0 ? null : (
                <span className="ml-2 font-mono text-micro text-paper-low">{failure.message}</span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * The gateway already triaged these against its own thresholds; this panel
 * renders that verdict rather than inventing a second one.
 */
function RisksPanel({ risks }: { readonly risks: readonly TriagedRisk[] }): JSX.Element {
  const t = useT();
  return (
    <Panel
      caption={t('health_perspective.risks.title')}
      count={risks.length}
      padded
      testId="health-perspective-risks"
    >
      {risks.length === 0 ? (
        <EmptyState testId="health-perspective-risks-empty" title={t('health_perspective.risks.empty')} />
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {risks.map((risk) => (
            <li
              key={risk.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-line-soft bg-well px-3 py-2"
              data-testid={`health-perspective-risk-${risk.id}`}
            >
              <Chip tone={risk.severity === 'critical' ? 'bad' : 'warn'}>{t(RISK_KIND_KEY[risk.kind])}</Chip>
              <span className="min-w-0 flex-1 truncate text-dense text-paper" title={risk.label}>
                {risk.label}
              </span>
              <span className="font-mono text-micro text-paper-low">
                {t('health_perspective.risks.threshold', {
                  current: formatRate(risk.currentRate),
                  threshold: formatRate(risk.thresholdRate),
                })}
              </span>
              <span className="font-mono text-micro text-paper-low">{formatInteger(risk.affectedCount)}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function LoadingView({ label }: { readonly label: string }): JSX.Element {
  return (
    <div className="flex flex-col gap-6" data-testid="health-perspective-loading">
      <span className="sr-only">{label}</span>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-8">
        {Array.from({ length: 8 }).map((_, index) => (
          <Skeleton key={index} className="h-24" />
        ))}
      </div>
      <Skeleton className="h-[32rem]" />
      <Skeleton className="h-64" />
    </div>
  );
}

/** Unknown / absent narrows to the 24h default; never throws. */
function parseWindow(raw: string | null): PerspectiveWindow {
  return PERSPECTIVE_WINDOWS.find((preset) => preset === raw) ?? DEFAULT_WINDOW;
}

/**
 * `?node=` carries an opaque `${kind}:${rawId}` node id. It is NOT validated
 * against the model here: a link written against a node that has since gone
 * idle should open the page, not 404 it. The inspector resolves the id and
 * falls back to its own empty state when it misses.
 */
function parseNode(raw: string | null): string | null {
  return raw === null || raw.length === 0 ? null : raw;
}
